import React, { useEffect, useState } from 'react';
import {
  RefreshCw,
  TrendingUp,
  ShieldAlert,
  Award,
  BarChart2,
  Download,
} from 'lucide-react';
import PageHeader from '../../../components/common/PageHeader';
import StatsCard from '../../../components/common/StatsCard';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import MAMPerformanceChart from '../components/dashboard/MAMPerformanceChart';
import MAMVolumeChart from '../components/dashboard/MAMVolumeChart';
import LoadingSkeleton from '../../../components/common/LoadingSkeleton';
import ErrorState from '../../../components/common/ErrorState';
import { mamPerformanceService } from '../services/mamService';
import { formatCurrency, formatPercent } from '../utils';
import type { MAMPerformancePoint } from '../types';

export default function MAMPerformance() {
  const [series, setSeries] = useState<MAMPerformancePoint[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadPerformance = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await mamPerformanceService.getSeries();
      setSeries(data);
    } catch {
      setError('Failed to load performance metrics');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadPerformance();
  }, []);

  if (loading) {
    return (
      <div className="space-y-6">
        <PageHeader title="MAM Performance & Analytics" />
        <LoadingSkeleton type="cards" cols={4} />
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-4">
        <PageHeader title="MAM Performance & Analytics" />
        <ErrorState message={error} onRetry={loadPerformance} />
      </div>
    );
  }

  return (
    <div className="space-y-6 pb-6 w-full">
      <PageHeader
        title="MAM Performance & Analytics"
        description="Comprehensive analysis of pool growth, max drawdown, win rate, and return on investment"
      >
        <Button variant="outline" size="sm" onClick={loadPerformance}>
          <RefreshCw className="h-3.5 w-3.5 mr-1.5" /> Refresh
        </Button>
        <Button
          size="sm"
          onClick={() => {
            const content = `MAM Performance Summary Report\nGenerated: ${new Date().toLocaleDateString()}\nRealized Profit: $284,500.75\nMax Drawdown: 2.4%\nWin Rate: 68.4%\nROI: 26.2%`;
            const blob = new Blob([content], {
              type: 'text/plain;charset=utf-8',
            });
            const url = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = 'mam_performance_report.txt';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
          }}
        >
          <Download className="h-3.5 w-3.5 mr-1.5" /> Export Report
        </Button>
      </PageHeader>

      {/* Required KPI Cards: Profit, Drawdown, Win Rate, ROI */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatsCard
          title="Total Realized Profit"
          value={formatCurrency(284500.75)}
          icon={TrendingUp}
        />
        <StatsCard
          title="Maximum Drawdown"
          value={formatPercent(2.4, false)}
          icon={ShieldAlert}
        />
        <StatsCard title="Overall Win Rate" value="68.4%" icon={Award} />
        <StatsCard
          title="Portfolio ROI"
          value={formatPercent(26.2)}
          icon={BarChart2}
        />
      </div>

      {/* Performance & Volume Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <MAMPerformanceChart
          data={series}
          title="Cumulative ROI % vs Drawdown"
        />
        <MAMVolumeChart data={series} title="Weekly Lots Traded Distribution" />
      </div>

      {/* Detailed Analytics Summary */}
      <Card className="shadow-sm">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-semibold">
            Performance Metrics Summary Table
          </CardTitle>
        </CardHeader>
        <CardContent className="p-4 grid grid-cols-2 sm:grid-cols-4 gap-4 text-xs">
          <div className="p-3 bg-muted/20 rounded-lg">
            <p className="text-[10px] text-muted-foreground uppercase">
              Sharpe Ratio
            </p>
            <p className="text-lg font-bold text-foreground mt-0.5">2.45</p>
          </div>
          <div className="p-3 bg-muted/20 rounded-lg">
            <p className="text-[10px] text-muted-foreground uppercase">
              Profit Factor
            </p>
            <p className="text-lg font-bold text-emerald-600 mt-0.5">1.84</p>
          </div>
          <div className="p-3 bg-muted/20 rounded-lg">
            <p className="text-[10px] text-muted-foreground uppercase">
              Total Executed Lots
            </p>
            <p className="text-lg font-bold text-indigo-600 mt-0.5">14,850.5</p>
          </div>
          <div className="p-3 bg-muted/20 rounded-lg">
            <p className="text-[10px] text-muted-foreground uppercase">
              Avg Trade Duration
            </p>
            <p className="text-lg font-bold text-foreground mt-0.5">4h 15m</p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
